import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { API_ENDPOINTS } from "@/constants/config"
import {
  Trash2, ArrowLeft, Plus, Loader2, Search, X, RefreshCw,
  Tag, Ruler, Package, Palette, Layers, ChevronDown, ChevronUp,
} from "lucide-react"
import toast from "react-hot-toast"

const normalizeName = (value) => value.trim().replace(/\s+/g, " ")

const hasDuplicateName = (items, value) => {
  const v = value.trim().toLowerCase()
  if (!v) return false
  return items.some((i) => i.name?.trim().toLowerCase() === v)
}

const InventorySettings = ({ hideHeader = false }) => {
  const navigate = useNavigate()

  const [categories, setCategories] = useState([])
  const [newCategory, setNewCategory] = useState("")
  const [units, setUnits] = useState([])
  const [newUnit, setNewUnit] = useState("")
  const [sizes, setSizes] = useState([])
  const [newSize, setNewSize] = useState("")
  const [brands, setBrands] = useState([])
  const [newBrand, setNewBrand] = useState("")
  const [colors, setColors] = useState([])
  const [newColor, setNewColor] = useState("")
  const [subCategories, setSubCategories] = useState([])

  const [initialLoading, setInitialLoading] = useState(true)
  const [featuresLoading, setFeaturesLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [isColorEnabled, setIsColorEnabled] = useState(false)
  const [updatingFeature, setUpdatingFeature] = useState(false)
  const [isSubCategoryEnabled, setIsSubCategoryEnabled] = useState(false)
  const [updatingSubCategoryFeature, setUpdatingSubCategoryFeature] = useState(false)

  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [creatingUnit, setCreatingUnit] = useState(false)
  const [deletingUnitId, setDeletingUnitId] = useState(null)
  const [creatingSize, setCreatingSize] = useState(false)
  const [deletingSizeId, setDeletingSizeId] = useState(null)
  const [creatingBrand, setCreatingBrand] = useState(false)
  const [deletingBrandId, setDeletingBrandId] = useState(null)
  const [creatingColor, setCreatingColor] = useState(false)
  const [deletingColorId, setDeletingColorId] = useState(null)

  const [openSection, setOpenSection] = useState("Categories")
  const [searchMap, setSearchMap] = useState({})

  // ── Sub-category management state ─────────────────────────────────────────
  const [expandedCategoryId, setExpandedCategoryId] = useState(null)
  const [subCategoryInputs, setSubCategoryInputs] = useState({})
  const [creatingSubCategoryFor, setCreatingSubCategoryFor] = useState(null)
  const [deletingSubCategoryId, setDeletingSubCategoryId] = useState(null)

  // ── Fetch helpers ──────────────────────────────────────────────────────────
  const fetchFeatureConfig = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventoryFeatureConfig, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error("Failed to load feature config")
      const data = await res.json()
      setIsColorEnabled(Boolean(data?.data?.features?.color))
      setIsSubCategoryEnabled(Boolean(data?.data?.features?.sub_category))
    } catch (err) {
      toast.error(err.message || "Failed to load feature config")
    } finally {
      setFeaturesLoading(false)
    }
  }

  const fetchList = async (endpoint, setter, label) => {
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error(`Failed to load ${label}`)
      const data = await res.json()
      setter(data.data || [])
    } catch (err) {
      toast.error(err.message || `Failed to load ${label}`)
    }
  }

  const fetchAll = () =>
    Promise.all([
      fetchFeatureConfig(),
      fetchList(API_ENDPOINTS.inventoryCategories, setCategories, "categories"),
      fetchList(API_ENDPOINTS.inventoryUnits, setUnits, "units"),
      fetchList(API_ENDPOINTS.inventorySizes, setSizes, "sizes"),
      fetchList(API_ENDPOINTS.inventoryBrands, setBrands, "brands"),
    ])

  useEffect(() => {
    const load = async () => {
      setInitialLoading(true)
      await fetchAll()
      setInitialLoading(false)
    }
    load()
  }, [])

  useEffect(() => {
    if (isColorEnabled) fetchList(API_ENDPOINTS.inventoryColors, setColors, "colors")
  }, [isColorEnabled])

  useEffect(() => {
    if (isSubCategoryEnabled) fetchList(API_ENDPOINTS.inventorySubCategories, setSubCategories, "sub-categories")
  }, [isSubCategoryEnabled])

  const handleRefreshAll = async () => {
    setRefreshing(true)
    await Promise.all([
      fetchAll(),
      ...(isColorEnabled ? [fetchList(API_ENDPOINTS.inventoryColors, setColors, "colors")] : []),
      ...(isSubCategoryEnabled ? [fetchList(API_ENDPOINTS.inventorySubCategories, setSubCategories, "sub-categories")] : []),
    ])
    setRefreshing(false)
    toast.success("Refreshed")
  }

  // ── Color feature toggle ───────────────────────────────────────────────────
  const handleToggleColorFeature = async (checked) => {
    try {
      setUpdatingFeature(true)
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventoryFeatureConfig, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ features: { color: checked } }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || "Failed to update")
      setIsColorEnabled(checked)
      if (checked) fetchList(API_ENDPOINTS.inventoryColors, setColors, "colors")
      toast.success(`Color ${checked ? "enabled" : "disabled"}`)
    } catch (err) {
      toast.error(err.message || "Failed to update")
    } finally {
      setUpdatingFeature(false)
    }
  }

  // ── Sub-category feature toggle ────────────────────────────────────────────
  const handleToggleSubCategoryFeature = async (checked) => {
    try {
      setUpdatingSubCategoryFeature(true)
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventoryFeatureConfig, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ features: { sub_category: checked } }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || "Failed to update")
      setIsSubCategoryEnabled(checked)
      if (checked) fetchList(API_ENDPOINTS.inventorySubCategories, setSubCategories, "sub-categories")
      else setExpandedCategoryId(null)
      toast.success(`Sub-category ${checked ? "enabled" : "disabled"}`)
    } catch (err) {
      toast.error(err.message || "Failed to update")
    } finally {
      setUpdatingSubCategoryFeature(false)
    }
  }

  // ── Sub-category add / delete ──────────────────────────────────────────────
  const handleAddSubCategory = async (categoryId) => {
    const name = normalizeName(subCategoryInputs[categoryId] || "")
    if (!name) return
    const existingForCategory = subCategories.filter((sc) => sc.category === categoryId)
    if (hasDuplicateName(existingForCategory, name)) {
      toast.error("Sub-category already exists for this category")
      return
    }
    try {
      setCreatingSubCategoryFor(categoryId)
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventorySubCategories, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, category: categoryId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || "Failed to add sub-category")
      setSubCategories((prev) => [...prev, data.data])
      setSubCategoryInputs((prev) => ({ ...prev, [categoryId]: "" }))
      toast.success("Sub-category added")
    } catch (err) {
      toast.error(err.message)
    } finally {
      setCreatingSubCategoryFor(null)
    }
  }

  const handleDeleteSubCategory = async (subCategory) => {
    if (!window.confirm(`Delete "${subCategory.name}"?`)) return
    try {
      setDeletingSubCategoryId(subCategory._id)
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventorySubCategory(subCategory._id), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || "Failed to delete sub-category")
      setSubCategories((prev) => prev.filter((x) => x._id !== subCategory._id))
      toast.success("Sub-category deleted")
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeletingSubCategoryId(null)
    }
  }

  // ── Generic add / delete ───────────────────────────────────────────────────
  const handleAdd = async ({ endpoint, value, items, setter, setCreatingState, setInputValue, label }) => {
    const name = normalizeName(value)
    if (!name) return
    if (hasDuplicateName(items, name)) { toast.error(`${label} already exists`); return }
    try {
      setCreatingState(true)
      const token = localStorage.getItem("token")
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || `Failed to add ${label}`)
      setter((prev) => [...prev, data.data].sort((a, b) => a.name.localeCompare(b.name)))
      setInputValue("")
      toast.success(`${label} added`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setCreatingState(false)
    }
  }

  const handleDelete = async ({ endpoint, id, name, setter, setDeletingState, label }) => {
    if (!window.confirm(`Delete "${name}"?`)) return
    try {
      setDeletingState(id)
      const token = localStorage.getItem("token")
      const res = await fetch(endpoint(id), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || `Failed to delete ${label}`)
      setter((prev) => prev.filter((x) => x._id !== id))
      toast.success(`${label} deleted`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeletingState(null)
    }
  }

  // ── Shared delete button for master list items ─────────────────────────────
  const renderDeleteButton = (item, section, extraClass = "") => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={`h-7 w-7 opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all ${extraClass}`}
      onClick={() =>
        handleDelete({
          endpoint: section.deleteEndpoint,
          id: item._id,
          name: item.name,
          setter: section.key === "Categories" ? setCategories
            : section.key === "Brands" ? setBrands
            : section.key === "Units of Measurement" ? setUnits
            : section.key === "Sizes" ? setSizes
            : setColors,
          setDeletingState: section.setDeletingState,
          label: section.label,
        })
      }
      disabled={section.deletingState === item._id}
    >
      {section.deletingState === item._id
        ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
        : <Trash2 className="h-3.5 w-3.5" />
      }
    </Button>
  )

  // ── Section definitions ────────────────────────────────────────────────────
  const sections = [
    {
      key: "Categories",
      label: "Category",
      icon: Layers,
      items: categories,
      inputValue: newCategory,
      setInputValue: setNewCategory,
      creatingState: creating,
      setCreatingState: setCreating,
      deletingState: deletingId,
      setDeletingState: setDeletingId,
      addEndpoint: API_ENDPOINTS.inventoryCategories,
      deleteEndpoint: API_ENDPOINTS.inventoryCategory,
      placeholder: "e.g. Electronics, Stationery",
    },
    {
      key: "Brands",
      label: "Brand",
      icon: Tag,
      items: brands,
      inputValue: newBrand,
      setInputValue: setNewBrand,
      creatingState: creatingBrand,
      setCreatingState: setCreatingBrand,
      deletingState: deletingBrandId,
      setDeletingState: setDeletingBrandId,
      addEndpoint: API_ENDPOINTS.inventoryBrands,
      deleteEndpoint: API_ENDPOINTS.inventoryBrand,
      placeholder: "e.g. Samsung, Dell, Classmate",
    },
    {
      key: "Units of Measurement",
      label: "Unit",
      icon: Ruler,
      items: units,
      inputValue: newUnit,
      setInputValue: setNewUnit,
      creatingState: creatingUnit,
      setCreatingState: setCreatingUnit,
      deletingState: deletingUnitId,
      setDeletingState: setDeletingUnitId,
      addEndpoint: API_ENDPOINTS.inventoryUnits,
      deleteEndpoint: API_ENDPOINTS.inventoryUnit,
      placeholder: "e.g. Pieces, Box, Kg, Ream",
    },
    {
      key: "Sizes",
      label: "Size",
      icon: Package,
      items: sizes,
      inputValue: newSize,
      setInputValue: setNewSize,
      creatingState: creatingSize,
      setCreatingState: setCreatingSize,
      deletingState: deletingSizeId,
      setDeletingState: setDeletingSizeId,
      addEndpoint: API_ENDPOINTS.inventorySizes,
      deleteEndpoint: API_ENDPOINTS.inventorySize,
      placeholder: "e.g. Small, Medium, XL, A4",
    },
    ...(isColorEnabled
      ? [{
          key: "Colors",
          label: "Color",
          icon: Palette,
          items: colors,
          inputValue: newColor,
          setInputValue: setNewColor,
          creatingState: creatingColor,
          setCreatingState: setCreatingColor,
          deletingState: deletingColorId,
          setDeletingState: setDeletingColorId,
          addEndpoint: API_ENDPOINTS.inventoryColors,
          deleteEndpoint: API_ENDPOINTS.inventoryColor,
          placeholder: "e.g. Blue, Red, Matte Black",
        }]
      : []),
  ]

  const setSearch = (key, val) => setSearchMap((prev) => ({ ...prev, [key]: val }))

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {!hideHeader && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => navigate("/dashboard/inventory")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <p className="text-sm font-semibold text-gray-900">Inventory Settings</p>
            <p className="text-xs text-gray-500 mt-0.5">Manage categories, brands, units, sizes & colors</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Color toggle */}
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50">
            <Palette className="h-3.5 w-3.5 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium">Colors</span>
            {(featuresLoading || updatingFeature) && (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            )}
            <Switch
              checked={isColorEnabled}
              onCheckedChange={handleToggleColorFeature}
              disabled={featuresLoading || updatingFeature || initialLoading}
              className="scale-75 origin-right"
            />
          </div>

          {/* Sub-category toggle */}
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50">
            <Layers className="h-3.5 w-3.5 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium">Sub-category</span>
            {(featuresLoading || updatingSubCategoryFeature) && (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            )}
            <Switch
              checked={isSubCategoryEnabled}
              onCheckedChange={handleToggleSubCategoryFeature}
              disabled={featuresLoading || updatingSubCategoryFeature || initialLoading}
              className="scale-75 origin-right"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs border-gray-200 text-gray-600"
            onClick={handleRefreshAll}
            disabled={initialLoading || refreshing}
          >
            {refreshing
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="h-3.5 w-3.5" />
            }
          </Button>
        </div>
      </div>

      {/* Loading skeleton */}
      {initialLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Loading settings…</span>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {sections.map((section) => {
            const Icon = section.icon
            const isOpen = openSection === section.key
            const query = searchMap[section.key] || ""
            const duplicate = hasDuplicateName(section.items, normalizeName(section.inputValue))
            const filtered = section.items
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .filter((item) => item.name.toLowerCase().includes(query.toLowerCase()))

            return (
              <div key={section.key}>
                {/* Accordion header */}
                <button
                  type="button"
                  onClick={() => setOpenSection(isOpen ? null : section.key)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{section.key}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {section.items.length} {section.items.length === 1 ? "item" : "items"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-6 min-w-[24px] px-2 rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                      {section.items.length}
                    </span>
                    {isOpen
                      ? <ChevronUp className="h-4 w-4 text-gray-400" />
                      : <ChevronDown className="h-4 w-4 text-gray-400" />
                    }
                  </div>
                </button>

                {/* Accordion body */}
                {isOpen && (
                  <div className="px-5 pb-5 bg-gray-50/60">
                    {/* Add input */}
                    <form
                      className="flex gap-2 mb-3"
                      onSubmit={(e) => {
                        e.preventDefault()
                        if (!section.creatingState && normalizeName(section.inputValue)) {
                          handleAdd({
                            endpoint: section.addEndpoint,
                            value: section.inputValue,
                            items: section.items,
                            setter: section.key === "Categories" ? setCategories
                              : section.key === "Brands" ? setBrands
                              : section.key === "Units of Measurement" ? setUnits
                              : section.key === "Sizes" ? setSizes
                              : setColors,
                            setCreatingState: section.setCreatingState,
                            setInputValue: section.setInputValue,
                            label: section.label,
                          })
                        }
                      }}
                    >
                      <div className="flex-1 relative">
                        <Input
                          placeholder={section.placeholder}
                          value={section.inputValue}
                          onChange={(e) => section.setInputValue(e.target.value)}
                          disabled={section.creatingState}
                          className={`h-9 text-sm ${duplicate ? "border-amber-400 bg-amber-50/40" : ""}`}
                        />
                        {duplicate && (
                          <p className="text-xs text-amber-600 mt-1">Already exists</p>
                        )}
                      </div>
                      <Button
                        type="submit"
                        size="sm"
                        className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                        disabled={section.creatingState || !normalizeName(section.inputValue) || duplicate}
                      >
                        {section.creatingState
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>
                        }
                      </Button>
                    </form>

                    {/* Search */}
                    {section.items.length > 5 && (
                      <div className="relative mb-3">
                        <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <Input
                          value={query}
                          onChange={(e) => setSearch(section.key, e.target.value)}
                          placeholder={`Search ${section.key.toLowerCase()}…`}
                          className="pl-8 pr-8 h-8 text-xs bg-white"
                        />
                        {query && (
                          <button
                            type="button"
                            onClick={() => setSearch(section.key, "")}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Items list */}
                    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                      {section.items.length === 0 ? (
                        <div className="py-10 flex flex-col items-center gap-1 text-center">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mb-1">
                            <Icon className="h-4 w-4 text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500 font-medium">No {section.key.toLowerCase()} yet</p>
                          <p className="text-xs text-gray-400">Use the field above to add one</p>
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-sm text-gray-500">No results for "{query}"</p>
                          <button
                            type="button"
                            onClick={() => setSearch(section.key, "")}
                            className="text-xs text-blue-600 hover:underline mt-1"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : (
                        <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                          {filtered.map((item) => {
                            if (section.key !== "Categories" || !isSubCategoryEnabled) {
                              return (
                                <li
                                  key={item._id}
                                  className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 group"
                                >
                                  <span className="text-sm text-gray-800">{item.name}</span>
                                  {renderDeleteButton(item, section)}
                                </li>
                              )
                            }

                            // Categories section with sub-category management enabled:
                            // each row expands to manage its own sub-categories.
                            const isExpanded = expandedCategoryId === item._id
                            const itemSubCategories = subCategories
                              .filter((sc) => sc.category === item._id)
                              .slice()
                              .sort((a, b) => a.name.localeCompare(b.name))

                            return (
                              <li key={item._id} className="px-4 py-2.5 hover:bg-gray-50 group">
                                <div className="flex items-center justify-between">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedCategoryId(isExpanded ? null : item._id)}
                                    className="flex items-center gap-2 text-sm text-gray-800 flex-1 text-left"
                                  >
                                    {isExpanded
                                      ? <ChevronUp className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                      : <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    }
                                    <span>{item.name}</span>
                                    <span className="text-xs text-gray-400">
                                      {itemSubCategories.length} sub-{itemSubCategories.length === 1 ? "category" : "categories"}
                                    </span>
                                  </button>
                                  {renderDeleteButton(item, section, "flex-shrink-0")}
                                </div>

                                {isExpanded && (
                                  <div className="mt-2.5 ml-5 pl-3 border-l-2 border-gray-100 space-y-2">
                                    <form
                                      className="flex gap-2"
                                      onSubmit={(e) => {
                                        e.preventDefault()
                                        handleAddSubCategory(item._id)
                                      }}
                                    >
                                      <Input
                                        value={subCategoryInputs[item._id] || ""}
                                        onChange={(e) =>
                                          setSubCategoryInputs((prev) => ({ ...prev, [item._id]: e.target.value }))
                                        }
                                        placeholder="e.g. Pens, Notebooks"
                                        disabled={creatingSubCategoryFor === item._id}
                                        className="h-8 text-xs bg-white"
                                      />
                                      <Button
                                        type="submit"
                                        size="sm"
                                        className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                                        disabled={creatingSubCategoryFor === item._id || !normalizeName(subCategoryInputs[item._id] || "")}
                                      >
                                        {creatingSubCategoryFor === item._id
                                          ? <Loader2 className="h-3 w-3 animate-spin" />
                                          : <Plus className="h-3 w-3" />
                                        }
                                      </Button>
                                    </form>

                                    {itemSubCategories.length === 0 ? (
                                      <p className="text-xs text-gray-400">No sub-categories yet</p>
                                    ) : (
                                      <ul className="space-y-1">
                                        {itemSubCategories.map((sc) => (
                                          <li key={sc._id} className="flex items-center justify-between group/sub">
                                            <span className="text-xs text-gray-600">{sc.name}</span>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6 opacity-0 group-hover/sub:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                              onClick={() => handleDeleteSubCategory(sc)}
                                              disabled={deletingSubCategoryId === sc._id}
                                            >
                                              {deletingSubCategoryId === sc._id
                                                ? <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                                                : <Trash2 className="h-3 w-3" />
                                              }
                                            </Button>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default InventorySettings
